/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import NavigationHeaderBrand from './NavigationHeaderBrand';
import NavigationHeaderUtilities from './NavigationHeaderUtilities';

/**
 * Defines the tooltip override classes consumed by the current navigation header content.
 */
interface TooltipOverrideClasses {
  popper: string;
  tooltip: string;
}

/**
 * Defines the props for the current-theme header content rendered inside the shared navigation shell.
 */
interface NavigationHeaderContentProps {
  Features?: ApplicationFeatures;
  collapsed: boolean;
  globalGroupTitle?: string;
  isViewingGlobalGroup?: boolean;
  onBrandClick: () => void;
  onOpenGroupChooser: () => void;
  onOpenTracking: () => void;
  onOpenTrackingFilter: () => void;
  onOpenTrackingSearch: () => void;
  tooltipClasses: TooltipOverrideClasses;
  trackingFilterBadgeCount?: number;
  trackingFilterSelected?: boolean;
  trackingSelected?: boolean;
}

const useStyles = makeStyles(() => ({
  headerContent : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%'
  }
}));

/**
 * Renders the current-theme header content for the suite side navigation.
 *
 * @param props Defines the collapsed state and current-only utility actions.
 * @returns The current-theme side navigation header content.
 */
const NavigationHeaderContent = ({
  Features,
  collapsed,
  globalGroupTitle,
  isViewingGlobalGroup = false,
  onBrandClick,
  onOpenGroupChooser,
  onOpenTracking,
  onOpenTrackingFilter,
  onOpenTrackingSearch,
  tooltipClasses,
  trackingFilterBadgeCount = 0,
  trackingFilterSelected = false,
  trackingSelected = false
}: NavigationHeaderContentProps) => {
  const classes = useStyles();

  return (
    <div className={classes.headerContent}>
      <NavigationHeaderBrand collapsed={collapsed}
        onBrandClick={onBrandClick} />
      <NavigationHeaderUtilities collapsed={collapsed}
        Features={Features}
        globalGroupTitle={globalGroupTitle}
        isViewingGlobalGroup={isViewingGlobalGroup}
        onOpenGroupChooser={onOpenGroupChooser}
        onOpenTracking={onOpenTracking}
        onOpenTrackingFilter={onOpenTrackingFilter}
        onOpenTrackingSearch={onOpenTrackingSearch}
        tooltipClasses={tooltipClasses}
        trackingFilterBadgeCount={trackingFilterBadgeCount}
        trackingFilterSelected={trackingFilterSelected}
        trackingSelected={trackingSelected} />
    </div>
  );
};

export default memo(NavigationHeaderContent);
